import React, { useEffect, useState } from 'react'
import { setGlobal, useGlobal } from 'reactn'
import { useLocation, useParams } from 'react-router-dom'
import ReceptionComponent from 'ui/pages/applicant/Reception'
import {
  ReceptionEmpty,
  ReceptionSubmitted,
  PreReservationExpired,
  PreReservationNotFound,
  ReservationReserved,
} from 'ui/pages/applicant/ReceptionMessage'
import type {
  MedicalInstitutionProps,
  ReceptionEmailSendFormProps,
  ReceptionSmsSendFormProps,
} from 'types/applicant'
import type { PreReservation } from 'types/med'
import { VerificationTypes } from 'types/common'
import Loading from 'ui/pages/applicant/Loading'
import { getMed, getPreReservation, postVerification } from './fetch'
import { parseDatetimeSecondString } from 'ui/utils/dateFormatUtils'
import { ApiErrorWithMessage, OfflineError } from 'app/client'
import { isPastJstNow } from 'ui/utils/jstDatetime'
import queryString from 'query-string'

const Reception: React.FC = () => {
  const { institution_access_key, provisional_reservation_access_key } =
    useParams<
      'institution_access_key' | 'provisional_reservation_access_key'
    >() as {
      institution_access_key: string
      provisional_reservation_access_key?: string
    }

  const location = useLocation()
  const query = queryString.parse(location.search)
  const company_key = query?.cky as string | undefined // NOTE: URLにckyパラメーターがあればそれをcompany_keyとしてPOST時に送信する
  const pharmacy = query?.pharmacy as string | undefined // NOTE: タヨリス判定専用パラメーターとしてPOST時に送信する（タヨリス専用）
  const sld_token = query?.sld_token as string | undefined // NOTE: つくば市SaluDi連携PJ専用パラメーターとしてPOST時に送信する（Smart One Health専用）

  const [offline] = useGlobal('offline')
  const [finished, setFinished] = useState<boolean>() //NOTE: データ送信が完了したかかどうか
  const [verificationType, setVerificationType] = useState<
    VerificationTypes | undefined
  >()
  const [notFoundError, setNotFoundError] = useState<boolean>(false)
  const [med, setMed] = useState<MedicalInstitutionProps | null>(null)
  const [preReservation, setPreReservation] = useState<
    PreReservation & { applicant_phone?: string } // NOTE: 念のため受診者に非公開にするためapplicant_phoneのデータはこのAPIでは受け取らない
  >()
  const [preReservationError, setPreReservationError] = useState<
    'expired' | 'notFound' | 'reserved'
  >()

  useEffect(() => {
    institution_access_key &&
      getMed(institution_access_key)
        .then(setMed)
        .catch((e) => {
          if (e instanceof ApiErrorWithMessage) {
            if (
              e.has({
                type: 'validation_error',
                key: 'institution_access_key',
                value: 'exists',
              })
            ) {
              setGlobal({ error: 'invalidUrl' })
              return
            }
          }
          throw Error('unknown server_error')
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institution_access_key])

  useEffect(() => {
    provisional_reservation_access_key &&
      getPreReservation(provisional_reservation_access_key)
        .then(setPreReservation)
        .catch((e) => {
          if (e instanceof ApiErrorWithMessage) {
            if (
              e.has({
                type: 'validation_error',
                key: [
                  'provisional_reservation_id',
                  'provisional_reservation_access_key',
                ],
                value: 'exists',
              })
            ) {
              setPreReservationError('notFound')
            }
            if (
              e.has({
                type: 'validation_error',
                key: 'provisional_reservation_access_key',
                value: 'reserved',
              })
            ) {
              setPreReservationError('reserved')
            }
            if (
              e.has({
                type: 'validation_error',
                key: 'provisional_reservation_access_key_expiration',
                value: 'expired',
              })
            ) {
              setPreReservationError('expired')
            }
            return
          }
          throw Error('unknown server_error')
        })
  }, [provisional_reservation_access_key])

  const submitHandler = (
    data: ReceptionEmailSendFormProps | ReceptionSmsSendFormProps
  ) => {
    setVerificationType(
      (data as ReceptionEmailSendFormProps).email !== undefined
        ? 'email'
        : 'sms'
    )
    return postVerification({
      institution_access_key,
      provisional_reservation_access_key,
      company_key,
      pharmacy,
      sld_token,
      ...data,
    })
      .then(() => setFinished(true))
      .catch((e) => {
        if (offline) {
          throw new OfflineError()
        }
        if (e instanceof ApiErrorWithMessage) {
          if (
            e.has({
              type: 'validation_error',
              key: 'provisional_reservation_access_key',
              value: 'reserved',
            })
          ) {
            setPreReservationError('reserved')
          } else if (
            e.has({
              type: 'validation_error',
              key: 'provisional_reservation_access_key',
              value: 'exists',
            })
          ) {
            setPreReservationError('notFound')
          } else if (
            e.has({
              type: 'validation_error',
              key: 'provisional_reservation_access_key_expiration',
              value: 'valid',
            })
          ) {
            setPreReservationError('expired')
          } else if (
            // NOTE: 送信処理でエラーが発生した場合は、ApiErrorWithMessageをthrowして、上位のcatchでエラー表示する
            e.has({
              type: 'server_error',
              key: ['email', 'sms'],
            })
          ) {
            return Promise.reject(new ApiErrorWithMessage(e.response, e.body))
          } else {
            setNotFoundError(true)
          }
        } else {
          throw Error('unknown server_error')
        }
      })
  }

  if (med && preReservationError) {
    if (preReservationError === 'expired') {
      return <PreReservationExpired med={med} />
    }
    if (preReservationError === 'notFound') {
      return <PreReservationNotFound med={med} />
    }
    if (preReservationError === 'reserved') {
      return <ReservationReserved med={med} type={verificationType} />
    }
  }
  if (med && preReservation) {
    if (
      isPastJstNow(
        parseDatetimeSecondString(preReservation.access_key_expiration)
      )
    ) {
      return <PreReservationExpired med={med} />
    }
  }

  if (institution_access_key && med) {
    if (finished) {
      return <ReceptionSubmitted med={med} type={verificationType} />
    }
    if (!med.departments.length || notFoundError) {
      return <ReceptionEmpty med={med} />
    }
    return (
      <ReceptionComponent
        onSubmit={submitHandler}
        med={med}
        preReservation={preReservation}
      />
    )
  }
  return <Loading />
}

export default Reception
